import React, { useState } from 'react';

import { Button, Flex, ScrollArea, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconCloud, IconPlus, IconServer } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DroxyModal } from 'components/Droxy/DroxyModal';
import { FullScreenLoader } from 'components/FullScreenLoader';
import { NoDataFound } from 'components/NoDataFound';
import { UpgradePlanBlurCard } from 'components/UpgradePlanBlurCard';
import { useSubscriptionContext } from 'contexts/SubscriptionContext';
import { SettingsSectionWrapper } from 'pages/Settings/SettingTabs/SettingsSectionWrapper';
import { WhiteLabelDomainCard } from 'pages/Settings/SettingTabs/WhiteLabelSettingsTab/WhiteLabelDomainCard';
import { useTranslation } from 'react-i18next';
import { NotificationService } from 'services/notification-service';
import { WhiteLabelService } from 'services/white-label-service';
import { WHITE_LABEL_DOMAINS_QUERY_KEY } from 'shared/constants/query-keys';
import { DOMAIN_MAX_LENGTH } from 'shared/constants/white-label-constant';
import { WhiteLabelDomain } from 'shared/interfaces/WhiteLabelDomain';
import { domainValidator, maxLengthValidator, requiredInputValidator } from 'shared/utils/form-validators';

export function WhiteLabelSettingsTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [subscription] = useSubscriptionContext();

  const [addDomainModalOpened, setAddDomainModalOpened] = useState(false);

  const whiteLabelDomainsQuery = useQuery({
    queryFn: WhiteLabelService.getAllWhiteLabelDomains,
    queryKey: [WHITE_LABEL_DOMAINS_QUERY_KEY],
    enabled: !!subscription.product.enableWhiteLabel
  });

  const addDomainMutation = useMutation({
    mutationFn: WhiteLabelService.addWhiteLabelDomain,
    onSuccess: (newWhiteLabelDomain: WhiteLabelDomain) => {
      closeModal();
      queryClient.setQueryData([WHITE_LABEL_DOMAINS_QUERY_KEY], (existingDomains: WhiteLabelDomain[] = []) => [
        ...existingDomains,
        newWhiteLabelDomain
      ]);
      NotificationService.showResult(true, 'FEED_BACK.WHITE_LABEL.SUCCESS.ADD_DOMAIN');
    },
    onError: () => NotificationService.showResult(false, 'FEED_BACK.WHITE_LABEL.FAILURE.ADD_DOMAIN')
  });

  const addDomainForm = useForm({
    initialValues: { value: '' },
    validate: {
      value: (value) =>
        requiredInputValidator(value) || domainValidator(value) || maxLengthValidator(value, DOMAIN_MAX_LENGTH)
    }
  });

  const closeModal = () => {
    addDomainForm.reset();
    setAddDomainModalOpened(false);
  };

  if (subscription.product.enableWhiteLabel && whiteLabelDomainsQuery.isInitialLoading) return <FullScreenLoader />;
  const whiteLabelDomains = whiteLabelDomainsQuery.data || [];

  return (
    <>
      <SettingsSectionWrapper h="100%" title={t('WHITE_LABEL.YOUR_DOMAIN')}>
        <UpgradePlanBlurCard
          message={t('WHITE_LABEL.UPGRADE.DESCRIPTION')}
          showCard={!subscription.product.enableWhiteLabel}>
          {(whiteLabelDomains.length || 0) === 0 && subscription.product.enableWhiteLabel ? (
            <NoDataFound
              h="100%"
              size="xs"
              icon={<IconServer />}
              title={t('NO_DATA_FOUND.WHITE_LABEL_DOMAINS.TITLE')}
              subTitle={t('NO_DATA_FOUND.WHITE_LABEL_DOMAINS.SUB_TITLE')}
              button={
                <Button leftIcon={<IconPlus />} size="xs" onClick={() => setAddDomainModalOpened(true)}>
                  {t('APP.ACTIONS.ADD')}
                </Button>
              }
            />
          ) : (
            <ScrollArea w="100%" h="100%">
              {whiteLabelDomains.map((domain: WhiteLabelDomain) => (
                <WhiteLabelDomainCard domain={domain} key={domain._id} />
              ))}
            </ScrollArea>
          )}
        </UpgradePlanBlurCard>
      </SettingsSectionWrapper>
      <DroxyModal
        title={t('WHITE_LABEL.ADD_DOMAIN.TITLE')}
        description={t('WHITE_LABEL.ADD_DOMAIN.DESCRIPTION')}
        titleIcon={<IconCloud />}
        size={500}
        opened={addDomainModalOpened}
        onClose={() => closeModal()}>
        <form onSubmit={addDomainForm.onSubmit((domain) => addDomainMutation.mutate(domain))}>
          <Flex direction="row" align="flex-start" w="100%" justify="space-around" gap={10}>
            <TextInput
              w="100%"
              withAsterisk
              label={t('WHITE_LABEL.ADD_DOMAIN.LABEL')}
              placeholder={t('WHITE_LABEL.ADD_DOMAIN.PLACEHOLDER')}
              {...addDomainForm.getInputProps('value')}
            />
            <Button loading={addDomainMutation.isLoading} mt={20} type="submit" leftIcon={<IconPlus />}>
              {t('APP.ACTIONS.ADD')}
            </Button>
          </Flex>
        </form>
      </DroxyModal>
    </>
  );
}
